import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface FileUploadProps {
  caseId: string;
  onFileUploaded: () => void;
  disabled?: boolean;
}

export function FileUpload({ caseId, onFileUploaded, disabled = false }: FileUploadProps) {
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploading(true);
    try {
      for (const file of acceptedFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `${caseId}/${fileName}`;

        // Upload file to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('case-files')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        // Create document record in the database
        const { error: dbError } = await supabase
          .from('documents')
          .insert({
            case_id: caseId,
            filename: file.name,
            // Set type to 'other' as a default valid value
            // In a production environment, you might want to add a dropdown
            // to let users select the specific document type
            type: 'other',
            metadata: {
              size: file.size,
              type: file.type,
              path: filePath
            }
          });

        if (dbError) throw dbError;
      }
      onFileUploaded();
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
    }
  }, [caseId, onFileUploaded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg']
    }
  });

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${isDragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400'}`}
      style={{ pointerEvents: disabled ? 'none' : 'auto' }}
      aria-disabled={disabled}
    >
      <input {...getInputProps()} disabled={disabled} />
      <div className="flex flex-col items-center space-y-2">
        <Upload className={`h-8 w-8 ${isDragActive ? 'text-indigo-500' : 'text-gray-400'}`} />
        {uploading ? (
          <p className="text-sm text-gray-600">Uploading files...</p>
        ) : isDragActive ? (
          <p className="text-sm text-indigo-600">Drop files here...</p>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              Drag & drop files here, or click to select
            </p>
            <p className="text-xs text-gray-500">
              Supports PDF documents and images (PNG, JPG)
            </p>
          </>
        )}
      </div>
    </div>
  );
}